import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clock, Loader2, ExternalLink, Sparkles } from 'lucide-react';
import { API_URL } from '../lib/api';
import { toast } from 'sonner';

/**
 * TrialBanner — shown on player dashboard while the user is in (or just past)
 * a Shot Lab trial. Displays days remaining and lets them open the Stripe
 * Customer Portal to manage their subscription / payment method.
 *
 * Hidden when the user has no trial_end set or the user is already on a
 * fully paid active plan (subscription_status === "active" with no trial_end).
 */
export default function TrialBanner({ user, onRefresh }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Verify Stripe checkout session on return from trial signup -> dashboard
  useEffect(() => {
    const sid = searchParams.get('session_id');
    const welcome = searchParams.get('welcome');
    if (welcome === 'trial' && sid) {
      setVerifying(true);
      fetch(`${API_URL}/iq-lab/verify-payment?session_id=${encodeURIComponent(sid)}`)
        .then(r => r.json())
        .then(() => {
          toast.success('Welcome to Shot Lab — your 7-day free trial has started');
          if (typeof onRefresh === 'function') onRefresh();
        })
        .catch(() => {})
        .finally(() => {
          setVerifying(false);
          // Clean the URL
          searchParams.delete('session_id');
          searchParams.delete('welcome');
          setSearchParams(searchParams, { replace: true });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return null;

  const trialEnd = user.trial_end;
  const status = user.subscription_status;

  // Only show for trial / trialing states (or expired-trial fallthrough)
  const isTrial = status === 'trial' || status === 'trialing' || status === 'past_due' || status === 'unpaid';
  if (!trialEnd && !isTrial) return null;
  if (!trialEnd) return null;

  const end = new Date(trialEnd);
  const now = new Date();
  const msLeft = end.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
  const expired = msLeft <= 0;

  const handleManage = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/billing/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ return_url: window.location.origin + '/dashboard' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to open billing portal');
      if (data.portal_url) window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e.message || 'Could not open billing portal');
    } finally {
      setLoading(false);
    }
  };

  // Color treatment changes based on urgency
  const tone = expired
    ? { bg: 'bg-red-500/10', border: 'border-red-500/30', icon: 'text-red-500', label: 'Trial ended' }
    : daysLeft <= 2
      ? { bg: 'bg-amber-500/10', border: 'border-amber-500/30', icon: 'text-amber-500', label: `${daysLeft} day${daysLeft === 1 ? '' : 's'} left` }
      : { bg: 'bg-primary/10', border: 'border-primary/30', icon: 'text-primary', label: `${daysLeft} days left` };

  return (
    <div
      className={`relative overflow-hidden rounded-xl border ${tone.border} ${tone.bg} p-4 md:p-5 mb-2`}
      data-testid="trial-banner"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className={`w-10 h-10 rounded-lg bg-card flex items-center justify-center flex-shrink-0 ${tone.icon}`}>
            {expired ? <Sparkles size={20} /> : <Clock size={20} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-[10px] uppercase tracking-widest font-bold ${tone.icon}`}
                data-testid="trial-days-left"
              >
                {tone.label}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Shot Lab Trial</span>
            </div>
            <h3 className="font-heading text-lg md:text-xl font-bold tracking-tight text-foreground mt-1">
              {expired
                ? 'Your free trial has ended'
                : daysLeft === 0
                  ? 'Last day of your free trial'
                  : daysLeft === 1
                    ? '1 day left of your free trial'
                    : `${daysLeft} days left of your free trial`}
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
              {user.subscription_type === 'shot_lab_beta_bundle'
                ? 'After your trial, $69.99/mo will be charged to your card on file. Cancel anytime.'
                : 'After your trial, $20/mo founding member pricing will be charged. Cancel anytime.'}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <button
            onClick={handleManage}
            disabled={loading || verifying}
            className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-foreground text-background hover:opacity-90 disabled:opacity-60 px-5 py-3 rounded-lg text-sm font-bold uppercase tracking-wider transition-opacity"
            style={{ color: undefined }}
            data-testid="trial-manage-subscription"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Opening…
              </>
            ) : (
              <>
                Manage Subscription <ExternalLink size={14} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
